// components/GiftRedemptionBanner.tsx
// Shows pending gift subscriptions and allows user to redeem them

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Gift,
  Crown,
  X,
  Sparkles,
  CheckCircle,
  Loader2,
  PartyPopper,
  Clock
} from 'lucide-react';
import { supabase } from '../lib/supabase';

interface PendingGift {
  id: string;
  gifted_by_name: string;
  duration_months: number;
  message: string | null;
  created_at: string;
  expires_at: string | null;
}

interface GiftRedemptionBannerProps {
  onRedeemed?: () => void; // Callback when gift is redeemed
}

export default function GiftRedemptionBanner({ onRedeemed }: GiftRedemptionBannerProps) {
  const [pendingGifts, setPendingGifts] = useState<PendingGift[]>([]);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);
  const [selectedGift, setSelectedGift] = useState<PendingGift | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [redeemSuccess, setRedeemSuccess] = useState(false);
  const [redeemResult, setRedeemResult] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadPendingGifts();
  }, []);

  const loadPendingGifts = async () => {
    try {
      const { data, error } = await supabase.rpc('get_pending_gifts');
      
      if (error) throw error;
      
      if (data?.success && data.gifts.length > 0) {
        setPendingGifts(data.gifts);
      }
    } catch (err) {
      console.error('Error loading pending gifts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewGift = (gift: PendingGift) => {
    setSelectedGift(gift);
    setShowModal(true);
    setError('');
    setRedeemSuccess(false);
  };

  const handleRedeem = async () => {
    if (!selectedGift) return;
    
    setRedeeming(true);
    setError('');
    
    try {
      const { data, error: rpcError } = await supabase.rpc('redeem_gift_subscription', {
        p_gift_id: selectedGift.id
      });
      
      if (rpcError) throw rpcError;
      
      if (data?.success) {
        setRedeemSuccess(true);
        setRedeemResult(data);
        // Remove from pending list
        setPendingGifts(prev => prev.filter(g => g.id !== selectedGift.id));
        // Callback to refresh subscription status
        if (onRedeemed) {
          setTimeout(() => {
            onRedeemed();
          }, 2000);
        }
      } else {
        setError(data?.error || 'Failed to redeem gift');
      }
    } catch (err: any) {
      console.error('Error redeeming gift:', err);
      setError(err.message || 'Failed to redeem gift');
    } finally {
      setRedeeming(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedGift(null);
    setRedeemSuccess(false);
    setRedeemResult(null);
    setError('');
  };

  const getDaysUntilExpiry = (expiresAt: string | null) => {
    if (!expiresAt) return null;
    const now = new Date();
    const expiry = new Date(expiresAt);
    const days = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return days;
  };

  // Don't render if loading or no pending gifts
  if (loading || pendingGifts.length === 0) {
    return null;
  }

  return (
    <>
      {/* Banner for each pending gift */}
      <AnimatePresence>
        {pendingGifts.map((gift, index) => {
          const daysLeft = getDaysUntilExpiry(gift.expires_at);
          
          return (
            <motion.div
              key={gift.id}
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ delay: index * 0.1 }}
              className="relative overflow-hidden rounded-2xl mb-4"
              style={{
                background: 'linear-gradient(135deg, #9333ea, #ec4899, #f97316)'
              }}
            >
              {/* Animated background particles */}
              <div className="absolute inset-0 overflow-hidden">
                {[...Array(5)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-2 h-2 bg-white/30 rounded-full"
                    style={{
                      left: `${20 + i * 15}%`,
                      top: `${30 + (i % 3) * 20}%`,
                    }}
                    animate={{
                      y: [-10, 10, -10],
                      opacity: [0.3, 0.6, 0.3],
                    }}
                    transition={{
                      duration: 2 + i * 0.5,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                  />
                ))}
              </div>

              <div className="relative p-4">
                <div className="flex items-start gap-4">
                  {/* Gift Icon */}
                  <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
                    <Gift className="w-7 h-7 text-white" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-white font-bold text-sm">🎁 You've Been Gifted!</span>
                      <Sparkles className="w-4 h-4 text-yellow-300" />
                    </div>
                    <h3 className="text-white font-bold text-lg">
                      {gift.duration_months} {gift.duration_months === 1 ? 'Month' : 'Months'} Pro Plan
                    </h3>
                    <p className="text-white/80 text-sm">
                      From: {gift.gifted_by_name || 'AgeQ Team'}
                    </p>
                    {daysLeft !== null && daysLeft <= 7 && (
                      <p className="text-yellow-200 text-xs mt-1 flex items-center gap-1">
                        <Clock size={12} />
                        Expires in {daysLeft} {daysLeft === 1 ? 'day' : 'days'}
                      </p>
                    )}
                  </div>

                  {/* Action Button */}
                  <button
                    onClick={() => handleViewGift(gift)}
                    className="px-4 py-2 bg-white text-purple-600 rounded-xl font-bold text-sm hover:bg-white/90 transition-colors flex-shrink-0"
                  >
                    View Gift
                  </button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>

      {/* Redemption Modal */}
      <AnimatePresence>
        {showModal && selectedGift && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={(e) => e.target === e.currentTarget && !redeeming && closeModal()}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl"
            >
              {/* Success State */}
              {redeemSuccess ? (
                <div className="p-8 text-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', damping: 15 }}
                    className="w-24 h-24 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center mx-auto mb-6"
                  >
                    <PartyPopper className="w-12 h-12 text-white" />
                  </motion.div>
                  
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Welcome to Pro! 🎉
                  </h2>
                  <p className="text-gray-600 mb-4">
                    Your {selectedGift.duration_months}-month Pro plan is now active!
                  </p>
                  
                  {redeemResult?.expires_at && (
                    <div className="bg-purple-50 rounded-xl p-4 mb-6">
                      <p className="text-sm text-gray-600">Pro access expires on</p>
                      <p className="text-lg font-bold text-purple-600">
                        {new Date(redeemResult.expires_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  )}
                  
                  <button
                    onClick={closeModal}
                    className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-xl hover:from-purple-700 hover:to-pink-700 transition-colors"
                  >
                    Start Exploring Pro Features
                  </button>
                </div>
              ) : (
                <>
                  {/* Header */}
                  <div 
                    className="p-6 text-center relative"
                    style={{
                      background: 'linear-gradient(135deg, #9333ea, #ec4899)'
                    }}
                  >
                    <button
                      onClick={closeModal}
                      disabled={redeeming}
                      className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                    >
                      <X size={18} className="text-white" />
                    </button>
                    
                    <motion.div
                      animate={{ rotate: [0, -10, 10, -10, 0] }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                      className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center mx-auto mb-4"
                    >
                      <Gift className="w-10 h-10 text-white" />
                    </motion.div>
                    
                    <h2 className="text-2xl font-bold text-white mb-1">
                      You've Got a Gift! 🎁
                    </h2>
                    <p className="text-white/80 text-sm">
                      Someone special wants you to go Pro
                    </p>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    {/* Gift Details */}
                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-5 mb-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-gray-600 text-sm">Gift From</span>
                        <span className="font-bold text-gray-900">
                          {selectedGift.gifted_by_name || 'AgeQ Team'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-gray-600 text-sm">Pro Duration</span>
                        <span className="font-bold text-purple-600 text-lg">
                          {selectedGift.duration_months} {selectedGift.duration_months === 1 ? 'Month' : 'Months'}
                        </span>
                      </div>
                      {selectedGift.expires_at && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600 text-sm">Redeem Before</span>
                          <span className="font-medium text-gray-700">
                            {new Date(selectedGift.expires_at).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Personal Message */}
                    {selectedGift.message && (
                      <div className="bg-gray-50 rounded-xl p-4 mb-4">
                        <p className="text-xs text-gray-500 mb-1">Personal Message:</p>
                        <p className="text-gray-700 italic">"{selectedGift.message}"</p>
                      </div>
                    )}

                    {/* Pro Features */}
                    <div className="mb-6">
                      <p className="text-sm font-semibold text-gray-700 mb-3">What you'll get:</p>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle size={16} className="text-green-500" />
                          <span>Unlimited protocol regenerations</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle size={16} className="text-green-500" />
                          <span>8 AI food scans per day</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle size={16} className="text-green-500" />
                          <span>Advanced analytics & insights</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle size={16} className="text-green-500" />
                          <span>Priority support</span>
                        </div>
                      </div>
                    </div>

                    {/* Error Message */}
                    {error && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-xl mb-4">
                        <p className="text-red-700 text-sm">{error}</p>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="space-y-3">
                      <button
                        onClick={handleRedeem}
                        disabled={redeeming}
                        className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-xl hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                      >
                        {redeeming ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Activating Pro...
                          </>
                        ) : (
                          <>
                            <Crown className="w-5 h-5" />
                            Redeem Gift & Go Pro
                          </>
                        )}
                      </button>
                      
                      <button
                        onClick={closeModal}
                        disabled={redeeming}
                        className="w-full py-3 text-gray-600 font-medium hover:text-gray-900 transition-colors"
                      >
                        Maybe Later
                      </button>
                    </div>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}